import {
  Body,
  Button,
  Caption,
  CustomLink,
  Flex,
  Glide,
  LazyImage,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Shape,
  SubTitle,
  Title,
  styled,
} from '@teko/ds-desktop';
import { TickFilled } from '@teko/ds-icon';
import { ProductPrice, formatCurrency } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { useCartV2, useTenantConfig } from 'contexts';
import {
  countAndGetBenefitDescription,
  getDiscountByCombo,
  getTotalAmount,
  getTotalDiscount,
} from 'helpers';
import { t } from 'i18n';
import { getConditionProductLinkObj } from 'modules/cart/utils';
import { useEffect, useMemo, useState } from 'react';
import { IComboPopupProduct } from 'stores/comboPopupStore';
import {
  IActiveCombo,
  IComboGroup,
  IComboWithKey,
  IConditionProduct,
  ISelectingProduct,
} from 'types';
import { PromotionBenefitInfo } from './PromotionBenefitInfo';

interface ComboSelectionModalProps {
  visible: boolean;
  selectingProduct?: Pick<ISelectingProduct, 'conditionKey'> & {
    sku: string;
  };
  comboGroup?: IComboGroup;
  activeCombo?: IActiveCombo;
  handleConfirm?: (selectingCombo?: IActiveCombo) => void;
  setActiveCombo: (combo?: IActiveCombo) => void;
  closeModal: (selectingCombo?: IActiveCombo) => void;
  setComboGroup: (comboGroup?: IComboGroup) => void;
  productCombos?: IComboWithKey[];
  showAddComboToCartButton?: boolean;
  showAllCombos?: boolean;
  /**
   * Add-to-cart mode (opened from "Add to cart" on a product card). When
   * provided: shows a "Sản phẩm đang xem" block + "Mua combo tiết kiệm hơn"
   * header, and the footer shows "Mua sản phẩm đang xem" + "Mua combo". Omitted
   * in the SGC product-detail flow, which keeps its original behavior.
   */
  viewingProduct?: IComboPopupProduct;
  /** Add only the viewing product to cart (the "Mua sản phẩm đang xem" button). */
  onBuyViewingProduct?: () => void;
}

export const ComboSelectionModal: React.FC<ComboSelectionModalProps> = (
  props,
) => {
  const {
    visible,
    activeCombo,
    selectingProduct,
    closeModal,
    comboGroup,
    setActiveCombo,
    setComboGroup,
    handleConfirm,
    productCombos,
    showAddComboToCartButton = true,
    showAllCombos,
    viewingProduct,
    onBuyViewingProduct,
  } = props;
  const isAddToCartMode = !!viewingProduct;
  const { onAddComboItems } = useCartV2();
  const { numberDisplayFormat, applyNewLogicToCalculateComboPrice = false } =
    useTenantConfig();
  // Index of condition in its combo.
  // If user do not select any condition, default is the first condition in combo immediately after main product (main product's index is 0)
  const [conditionIndex, setConditionIndex] = useState(
    selectingProduct ? selectingProduct.conditionKey - 1 : 1,
  );
  const [selectingComboProduct, setSelectingComboProduct] = useState<{
    sku: string;
    conditionKey: number;
  }>();
  const [selectingCombo, setSelectingCombo] = useState(activeCombo);

  const updateActiveAndGroupCombo = () => {
    if (comboGroup && selectingCombo && selectingComboProduct) {
      const selectedGroup = comboGroup[selectingCombo.totalProduct];
      setActiveCombo(selectingCombo);
      setComboGroup({
        ...comboGroup,
        [selectingCombo.totalProduct]: {
          combos: selectedGroup.combos.map((cb) =>
            cb.key === selectingCombo.key ? selectingCombo : cb,
          ),
        },
      });
    }
  };

  const addComboToCart = async () => {
    if (selectingCombo) {
      updateActiveAndGroupCombo();
      const mainPayload = {
        override: true,
        groups: [
          {
            quantity: 1,
            promotionId: selectingCombo.promotion.id,
            products: selectingCombo.conditions.map((el, index) => ({
              sku: el.product?.sku,
              promotionId: !!el.product?.bestPromotionIds?.length
                ? el.product?.bestPromotionIds?.[0]
                : null,
              quantity: el.requiredQuantity,
              isPrimary: index === 0,
            })),
          },
        ],
      };

      await onAddComboItems(mainPayload, false);
      closeModal(selectingCombo);
    }
  };

  // Trigger open this modal: pick a sensible default combo. In add-to-cart mode
  // the popup edits the slot after the main product (conditionIndex), so prefer
  // the first combo that actually has a product there — otherwise the footer
  // total/checkmark bind to a single-product "buy Nx" combo that is hidden from
  // the list (and can show a negative total). Outside add-to-cart mode this
  // keeps the original behaviour (default to the first combo when none is set).
  useEffect(() => {
    if (!visible || !productCombos?.length) return;
    // A selection that already has a product at the editing slot is fine.
    if (activeCombo && activeCombo.conditions.length > conditionIndex) return;
    const source = isAddToCartMode
      ? productCombos.find((c) => c.conditions.length > conditionIndex) ??
        (activeCombo ? undefined : productCombos[0])
      : activeCombo
      ? undefined
      : productCombos[0];
    if (!source) return;
    const comboTemp = {
      ...source,
      conditions: source.conditions.map(({ products, ...restCondition }) => ({
        ...restCondition,
        product: products[0],
      })),
    };
    const defaultCombo = {
      ...comboTemp,
      discountByCombo: getDiscountByCombo(
        comboTemp,
        applyNewLogicToCalculateComboPrice,
      ),
      totalDiscount: getTotalDiscount(
        comboTemp,
        applyNewLogicToCalculateComboPrice,
      ),
      totalAmount: getTotalAmount(
        comboTemp,
        applyNewLogicToCalculateComboPrice,
      ),
    };
    setActiveCombo(defaultCombo);
    setSelectingCombo(defaultCombo);
    setSelectingComboProduct({
      sku: defaultCombo.conditions[conditionIndex]?.product?.sku,
      conditionKey: defaultCombo.conditions[conditionIndex]?.key,
    });
    // Default only on open / when the active combo changes; conditionIndex is
    // intentionally excluded so navigating slots doesn't re-default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCombo, visible]);

  useEffect(() => {
    if (!selectingComboProduct) {
      if (selectingProduct) {
        setSelectingComboProduct(selectingProduct);
      } else {
        if (activeCombo) {
          setSelectingComboProduct({
            sku: activeCombo.conditions[1]?.product?.sku,
            conditionKey: activeCombo.conditions[1]?.key,
          });
        }
      }
    }
  }, [activeCombo, selectingProduct]);

  const selectComboProduct = (
    product: IConditionProduct,
    cb: IComboWithKey,
  ) => {
    if (selectingCombo) {
      // build new active combo based on the new selected product
      const comboTemp = {
        ...cb,
        // Check if the select combo product in still in side the previous combo
        conditions:
          selectingCombo.key === cb.key
            ? //If yes, only update new product to current active index & not change other indexes
              selectingCombo.conditions.map((condition, index) => {
                return {
                  key: condition.key,
                  requiredQuantity: condition.requiredQuantity,
                  product:
                    index === conditionIndex ? product : condition.product,
                };
              })
            : // If not, update new product to current active index &
              // reset other indexes in active combo to products[0]
              cb.conditions.map((condition, index) => {
                return {
                  key: condition.key,
                  requiredQuantity: condition.requiredQuantity,
                  product:
                    index === conditionIndex ? product : condition.products[0],
                };
              }),
      };

      const newActiveCombo = {
        ...comboTemp,
        discountByCombo: getDiscountByCombo(
          comboTemp,
          applyNewLogicToCalculateComboPrice,
        ),
        totalDiscount: getTotalDiscount(
          comboTemp,
          applyNewLogicToCalculateComboPrice,
        ),
        totalAmount: getTotalAmount(
          comboTemp,
          applyNewLogicToCalculateComboPrice,
        ),
      };

      setSelectingCombo(newActiveCombo);
      setSelectingComboProduct({
        sku: product.sku,
        conditionKey: cb.conditions[conditionIndex].key,
      });
    }
  };

  // List of combos have same product quantity with active combo
  const similarCombos = useMemo(() => {
    if (selectingCombo && productCombos?.length) {
      return showAllCombos
        ? productCombos
        : productCombos.filter(
            (item) => item.totalProduct === selectingCombo.totalProduct,
          );
    }
    return [];
  }, [selectingCombo, productCombos]);

  return selectingCombo ? (
    <Modal
      visible={
        visible && !!activeCombo && (isAddToCartMode || !!selectingProduct)
      }
      overlayAlignItems="start"
      overlayJustifyContent="end"
      onClose={closeModal}
      footer={false}
      style={{ height: '100vh', padding: '16px' }}
      contentStyle={{ height: '100%', padding: '16px' }}
      {...getTrackingData({
        isTrackContent: true,
        contentName: 'popupChangeProductCombo',
        regionName: 'detailProductComboList',
      })}
    >
      <Flex direction="column" align="stretch" height="calc(100vh - 64px)">
        <ModalHeader
          style={{
            textAlign: 'start',
            margin: '0 -16px',
            padding: '0 16px 16px',
            borderBottom: '1px solid #E4E5F0',
          }}
          marginBottom="s16"
        >
          {isAddToCartMode ? (
            <Flex direction="column" gap="s4">
              <Title style={{ fontWeight: 500 }}>
                {t('Mua combo tiết kiệm hơn')}
              </Title>
              <Caption level={1} color="textSecondary">
                {t('Các combo khác nhau có thể có ưu đãi khác nhau')}
              </Caption>
            </Flex>
          ) : (
            <Title style={{ fontWeight: 500 }}>
              {t('Chọn sản phẩm trong Combo')}
            </Title>
          )}
        </ModalHeader>
        <StyledModalBody
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '16px',
            margin: '0 -16px',
          }}
        >
          {isAddToCartMode && viewingProduct && (
            <>
              <SubTitle level={2} marginBottom="s8">
                {t('Sản phẩm đang xem')}
              </SubTitle>
              <Shape
                border="1px solid divider"
                borderRadius="xl"
                padding="s12"
                marginBottom="s16"
              >
                <Flex gap="s12" align="center">
                  <Shape width="96px" height="96px" style={{ flexShrink: 0 }}>
                    <LazyImage
                      src={viewingProduct.imageUrl}
                      width="100%"
                      aspectRatio={1}
                      contain
                      alt={viewingProduct.name}
                    />
                  </Shape>
                  <Flex direction="column" gap="s4" style={{ flex: 1 }}>
                    <Caption level={2} line={2} color="textPrimary">
                      {viewingProduct.name}
                    </Caption>
                    {viewingProduct.uom && (
                      <Caption level={1} color="textSecondary">
                        {`${t('Đơn vị tính')}: ${viewingProduct.uom}`}
                      </Caption>
                    )}
                    <ProductPrice
                      latestPrice={viewingProduct.price?.latestPrice ?? 0}
                      supplierRetailPrice={
                        viewingProduct.price?.supplierRetailPrice
                      }
                      numberDisplayFormat={numberDisplayFormat}
                    />
                  </Flex>
                </Flex>
              </Shape>
              <SubTitle level={2} marginBottom="s12">
                {t('Chọn thêm sản phẩm trong combo')}
              </SubTitle>
            </>
          )}
          {similarCombos
            // Drop combos with no product at the editing slot up front so the
            // visible cards are numbered sequentially (Combo 1, 2, 3…) — matching
            // mobile. Skipping inside .map() would leave gaps in the numbering.
            .filter(
              (combo) => combo.conditions[conditionIndex]?.products.length,
            )
            .map((combo, index, visibleCombos) => {
              const isSelectingCombo =
                combo.key === selectingCombo?.key &&
                combo.conditions[conditionIndex].key ===
                  selectingComboProduct?.conditionKey &&
                combo.conditions[conditionIndex]?.products.findIndex(
                  (p) => p.sku === selectingComboProduct?.sku,
                ) > -1;
              return (
                <Shape
                  key={`${index}${combo.key}`}
                  borderRadius="xl"
                  border="1px solid divider"
                  marginBottom={
                    index !== visibleCombos.length - 1 ? 's16' : 's0'
                  }
                  {...getTrackingData({
                    isTrackContent: true,
                    contentName: `blockComboPromotion${index + 1}`,
                    regionName: 'detailProductComboList',
                    payload: JSON.stringify({ comboIndex: index + 1 }),
                  })}
                >
                  <Flex
                    align="center"
                    justify="space-between"
                    backgroundColor="background"
                    padding="s8"
                    style={{ borderRadius: '16px 16px 0px 0px' }}
                  >
                    <SubTitle level={2} marginLeft="s4">
                      Combo {index + 1}
                    </SubTitle>
                    {isSelectingCombo ? (
                      <TickFilled size="24" color="primary" />
                    ) : (
                      <Shape
                        height="24px"
                        width="24px"
                        border="1px solid disable"
                        style={{ borderRadius: '9999px' }}
                      />
                    )}
                  </Flex>
                  {combo.promotion.benefit &&
                    countAndGetBenefitDescription(
                      combo.promotion.benefit,
                      numberDisplayFormat,
                    ).numberOfBenefit >= 1 && (
                      <Shape padding="s8">
                        <PromotionBenefitInfo
                          promotionBenefit={combo.promotion.benefit}
                          showFullBenefits={true}
                        />
                      </Shape>
                    )}
                  <StyledGlide perView={2.3} displayNewLayout={true}>
                    {combo.conditions[conditionIndex]?.products.map(
                      (product, productIndex) => {
                        const productLink = getConditionProductLinkObj(product);
                        const isSelectedProduct =
                          combo.key === selectingCombo?.key &&
                          combo.conditions[conditionIndex].key ===
                            selectingComboProduct?.conditionKey &&
                          product.sku === selectingComboProduct?.sku;
                        const latestPrice =
                          applyNewLogicToCalculateComboPrice ||
                          !combo.promotion.condition?.exclusions?.length
                            ? product.price.latestPrice
                            : product.price.supplierRetailPrice;
                        const requiredQuantity =
                          combo.conditions[conditionIndex].requiredQuantity;
                        return (
                          <Shape
                            key={`${index}${productIndex}${product.sku}`}
                            backgroundColor="white"
                            borderRadius="s"
                            padding="s8"
                            style={{ position: 'relative' }}
                          >
                            <CustomLink {...productLink}>
                              <LazyImage
                                src={product.imageUrl}
                                hover="zoom"
                                contain
                                alt={product.name}
                                aspectRatio={1}
                                width="100%"
                              />
                              {requiredQuantity > 1 && (
                                <QuantityTag backgroundColor="primary500">
                                  {requiredQuantity}x
                                </QuantityTag>
                              )}
                              <Body
                                hoverColor="primary500"
                                marginTop="s8"
                                level={1}
                                line={3}
                                color="#434657"
                                height={60}
                              >
                                {product.name}
                              </Body>
                              <div
                                style={{ width: '100%', alignItems: 'center' }}
                              >
                                <ProductPrice
                                  latestPrice={latestPrice}
                                  supplierRetailPrice={
                                    product.price.supplierRetailPrice >
                                    latestPrice
                                      ? product.price.supplierRetailPrice
                                      : undefined
                                  }
                                  numberDisplayFormat={numberDisplayFormat}
                                />
                              </div>
                            </CustomLink>
                            <Shape
                              {...getTrackingData({
                                isTrackContent: true,
                                contentName: 'selectProduct',
                                regionName: 'detailProductComboList',
                                payload: JSON.stringify({
                                  comboIndex: index + 1,
                                  skuIndex: productIndex + 1,
                                  sku: product.sku,
                                }),
                              })}
                            >
                              <Button
                                variant="secondary"
                                width="100%"
                                marginTop="s8"
                                onClick={() =>
                                  selectComboProduct(product, combo)
                                }
                                disabled={isSelectedProduct}
                              >
                                <SubTitle level={4} weight="medium">
                                  {isSelectedProduct ? t('Đã chọn') : t('Chọn')}
                                </SubTitle>
                              </Button>
                            </Shape>
                          </Shape>
                        );
                      },
                    )}
                  </StyledGlide>
                </Shape>
              );
            })}
        </StyledModalBody>
        <ModalFooter
          borderTop="1px solid #E4E5F0"
          style={{
            padding: '16px 0 0 0',
            flexDirection: 'column',
            margin: '0 -16px',
            boxShadow: '0px -4px 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Flex gap="s12" height="48px" width="100%" padding={['s0', 's16']}>
            {selectingCombo.conditions.map((condition, index) => {
              return (
                <Shape
                  {...getTrackingData({
                    isTrackContent: true,
                    contentName: 'changeProductInCombo',
                    regionName: 'detailProductComboList',
                    payload: JSON.stringify({
                      skuComboIndex: index + 1,
                    }),
                  })}
                  key={`${index}${condition.key}`}
                  padding="s4"
                  height="100%"
                  width="48px"
                  border={
                    index === conditionIndex
                      ? '1px solid primary'
                      : '1px solid divider'
                  }
                  borderRadius="m"
                  opacity={index === 0 ? 'l' : undefined}
                >
                  <LazyImage
                    key={index}
                    src={condition.product?.imageUrl}
                    borderRadius={8}
                    contain
                    width="100%"
                    aspectRatio={1}
                    onClick={
                      index !== 0
                        ? () => {
                            setConditionIndex(index);
                            setSelectingComboProduct({
                              sku: condition.product?.sku,
                              conditionKey: condition.key,
                            });
                          }
                        : undefined
                    }
                  />
                </Shape>
              );
            })}
          </Flex>
          {isAddToCartMode ? (
            <Flex direction="column" gap="s12" width="100%" padding="s16">
              <Shape>
                <Flex align="center">
                  <Body level={1} marginRight="s4">{`${t('Tổng tiền')}:`}</Body>
                  <SubTitle level={1} color="primary500">
                    {formatCurrency(
                      selectingCombo.totalAmount,
                      numberDisplayFormat,
                    )}
                  </SubTitle>
                </Flex>
                <Flex>
                  <Body color="secondary" level={1} marginRight="s8">{`${t(
                    'Tiết kiệm',
                  )}:`}</Body>
                  <Body color="secondary" level={1}>
                    {formatCurrency(
                      selectingCombo.totalDiscount,
                      numberDisplayFormat,
                    )}
                  </Body>
                </Flex>
              </Shape>
              <Flex gap="s8" width="100%">
                <div style={{ flex: 1 }}>
                  <Button
                    variant="secondary"
                    width="100%"
                    onClick={onBuyViewingProduct}
                    borderRadius="m"
                  >
                    {t('Mua sản phẩm đang xem')}
                  </Button>
                </div>
                <Shape
                  style={{ flex: 1 }}
                  {...getTrackingData({
                    isTrackContent: true,
                    contentName: 'btnAddToCart',
                    regionName: 'detailProductComboList',
                    payload: JSON.stringify({
                      promotionType: 'combo',
                      comboTab: `Combo ${selectingCombo.totalProduct} sản phẩm`,
                      comboPromotion: selectingCombo.promotion.id,
                      comboIndex: selectingCombo.key,
                      skus: selectingCombo.conditions.map(
                        (c) => c.product?.sku,
                      ),
                    }),
                  })}
                >
                  <Button
                    variant="primary"
                    width="100%"
                    onClick={addComboToCart}
                    borderRadius="m"
                  >
                    {t('Mua combo')}
                  </Button>
                </Shape>
              </Flex>
            </Flex>
          ) : (
            <Flex direction="column" gap="s12" width="100%" padding="s16">
              <Shape>
                <Flex>
                  <Body level={1} marginRight="s4">{`${t('Tổng tiền')}:`}</Body>
                  <SubTitle level={1} color="primary500">
                    {formatCurrency(
                      selectingCombo.totalAmount,
                      numberDisplayFormat,
                    )}
                  </SubTitle>
                </Flex>
                <Flex>
                  <Body color="secondary" level={1} marginRight="s8">{`${t(
                    'Tiết kiệm',
                  )}:`}</Body>
                  <Body color="secondary" level={1}>
                    {formatCurrency(
                      selectingCombo.totalDiscount,
                      numberDisplayFormat,
                    )}
                  </Body>
                </Flex>
              </Shape>
              <Flex gap="s8" width="100%">
                {showAddComboToCartButton && (
                  <Shape
                    style={{ flex: 1 }}
                    {...getTrackingData({
                      isTrackContent: true,
                      contentName: 'btnAddToCart',
                      regionName: 'detailProductComboList',
                      payload: JSON.stringify({
                        promotionType: 'combo',
                        comboTab: `Combo ${selectingCombo.totalProduct} sản phẩm`,
                        comboPromotion: selectingCombo.promotion.id,
                        comboIndex: selectingCombo.key,
                        skus: selectingCombo.conditions.map(
                          (c) => c.product?.sku,
                        ),
                      }),
                    })}
                  >
                    <Button
                      variant="secondary"
                      width="100%"
                      borderRadius="m"
                      onClick={addComboToCart}
                    >
                      {t('Thêm vào giỏ hàng')}
                    </Button>
                  </Shape>
                )}
                <Shape
                  style={{ flex: 1 }}
                  {...getTrackingData({
                    isTrackContent: true,
                    contentName: 'btnConfirmCombo',
                    regionName: 'detailProductComboList',
                    payload: JSON.stringify({
                      comboPromotion: selectingCombo.promotion.id,
                    }),
                  })}
                >
                  <Button
                    variant="primary"
                    width="100%"
                    borderRadius="m"
                    onClick={() => {
                      updateActiveAndGroupCombo();
                      handleConfirm?.(selectingCombo);
                      closeModal(selectingCombo);
                      setSelectingComboProduct(undefined);
                      setSelectingCombo(undefined);
                    }}
                  >
                    {t('Xác nhận')}
                  </Button>
                </Shape>
              </Flex>
            </Flex>
          )}
        </ModalFooter>
      </Flex>
    </Modal>
  ) : null;
};

const StyledModalBody = styled(ModalBody)`
  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #bfbfbf;
  }
`;

const QuantityTag = styled(Shape)`
  position: absolute;
  height: 16px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  color: white;
  padding: 2px 5px;
  top: 8px;
  left: 8px;
`;

const StyledGlide = styled(Glide)`
  padding: 8px;
  .glide__track {
    padding: 8px;
    background-color: #f5f5f5;
    border-radius: 12px;
  }
  .glide__slides {
    margin: 0px;
  }
`;
